import { initSentry } from '@core/observability/sentry.js';
import { setupTagsFromConsent, syncTagsToConsent } from '@core/tags/index.js';
import { clientEnv } from '@core/types/env.js';
import { RouterProvider } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';

import '../index.css';
import { registerChunkLoadRecovery } from './app/chunk-reload.js';
import { registerNewVersionHandling } from './app/new-version.js';
import { AppProviders } from './app/providers.js';
import { router } from './app/router.js';
import { ConsentProvider } from './features/consent/ConsentProvider.js';
import { NomaConsentBanner } from './features/consent/NomaConsentBanner.js';
import { NomaConsentDialog } from './features/consent/NomaConsentDialog.js';
import { t } from './translations.js';

// Initialise before rendering so render-time crashes are captured. No-op in
// plain local dev unless the Spotlight sidecar is running (`npm run spotlight`).
initSentry({
  environment: clientEnv.VITE_SENTRY_ENVIRONMENT,
  dsn: clientEnv.VITE_SENTRY_DSN,
  release: clientEnv.VITE_SENTRY_RELEASE,
  spotlightPort: clientEnv.VITE_SPOTLIGHT_PORT,
  apiUrl: clientEnv.VITE_API_URL,
});

// Recover automatically when a deploy rotates chunk hashes and a stale tab
// requests a now-404 lazy-route chunk — and, before it comes to that, notice
// the newer deploy and move the tab onto it. The release SHA doubles as the
// build id `version.json` is compared against, both for the proactive check
// and for telling a stale chunk from a plain fetch failure.
registerChunkLoadRecovery({ buildId: clientEnv.VITE_SENTRY_RELEASE });
registerNewVersionHandling({ router, buildId: clientEnv.VITE_SENTRY_RELEASE });

const root = document.getElementById('root');
if (!root) throw new Error('No #root element');
createRoot(root).render(
  <ConsentProvider
    translations={t.consent}
    callbacks={{
      // c15t fires `onConsentSet` on hydration of stored state AND on every
      // explicit save; `onConsentChanged` fires only on transitions. The
      // tag container in `core/tags/` fans both payloads out to every
      // registered third-party tag — this file doesn't know which vendors
      // are loaded.
      onConsentSet: setupTagsFromConsent,
      onConsentChanged: syncTagsToConsent,
    }}
  >
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
    <NomaConsentBanner />
    <NomaConsentDialog />
  </ConsentProvider>,
);
